import AxiosService from '@/api/ApiBase';
import { FilterOptionsParams, FiltersService } from '@/types/service/filters.service';
import { OnHandEntity } from '@/types/on-hand/on-hand.entity';
import { OnHandFilters } from '@/types/on-hand/on-hand.filters';
import { OnHandAttachment } from '@/types/on-hand/on-hand.attachment';
import { AxiosRequestConfig } from 'axios';
import { ChangesService } from '@/types/service/changes.service';
import { ChargeEntity } from '@/types/charge/charge.entity';
import { OnHandFinalizePayload, OnHandFinalizeResponse } from '@/types/on-hand/on-hand.finalize';
import { VirtualSearchPayload, VirtualSearchResponse } from '@/types/virtual-search';
import { IpiInventoryFilterStatus } from '@/types/ipi/inventory/ipi.inventory.types';
import { TransferCustomerPayload } from '@/types/on-hand/on-hand.transfer';

const ON_HAND_BASE_URL = 'warehouse/export/onhand';

// const ON_HAND_BASE_URL = 'http://localhost:3000/api/export/onhand';

class OnHandService implements FiltersService<OnHandEntity, OnHandFilters>, ChangesService {
  create(data: any) {
    return AxiosService.post(`${ON_HAND_BASE_URL}/create`, data);
  }

  delete(onHandId: OnHandEntity['_id']) {
    return AxiosService.delete(`${ON_HAND_BASE_URL}/delete/${onHandId}`);
  }

  getOneById(id: string) {
    return AxiosService.get(`${ON_HAND_BASE_URL}/get`, {
      params: {
        onHandId: id,
      },
    });
  }

  getOneByNumber(number: string) {
    return AxiosService.get(`${ON_HAND_BASE_URL}/get`, {
      params: {
        onHandNumber: number,
      },
    });
  }

  update(data: any) {
    return AxiosService.put(`${ON_HAND_BASE_URL}/update`, data);
  }

  getFilteredItems(filters: OnHandFilters) {
    return AxiosService.post(`${ON_HAND_BASE_URL}/filter`, filters);
  }

  getFilterOptions<T>(params: Omit<FilterOptionsParams, 'limit'>) {
    return AxiosService.get<T>(`${ON_HAND_BASE_URL}/filter/options`, {
      params: { ...params, limit: 100 },
    });
  }

  virtualSearch(payload: VirtualSearchPayload<OnHandEntity>) {
    return AxiosService.post<VirtualSearchResponse<OnHandEntity>>(
      'warehouse/v2/export/onHand/search',
      payload
    );
  }

  virtualInventorySearch(
    payload: VirtualSearchPayload<OnHandEntity> & {
      'consignedTo.id'?: string;
      inventoryStatus?: IpiInventoryFilterStatus;
    }
  ) {
    if (!payload?.['inventoryStatus']) throw new Error('Missing Status');
    if (!payload?.['consignedTo.id']) throw new Error('Missing Customer Id');
    return AxiosService.post<VirtualSearchResponse<OnHandEntity>>(
      'warehouse/v2/export/inventory/search',
      payload
    );
  }

  getAttachments(id: string) {
    return AxiosService.get<OnHandAttachment[]>(`${ON_HAND_BASE_URL}/attachments/${id}`);
  }

  getMilestones(id: any) {
    return AxiosService.get(`${ON_HAND_BASE_URL}/milestones`, {
      params: {
        onHandId: id,
      },
    });
  }

  uploadAttachments(attachments: FormData, config: AxiosRequestConfig) {
    return AxiosService.put<OnHandAttachment[]>(
      `${ON_HAND_BASE_URL}/upload/attachments`,
      attachments,
      config
    );
  }

  getCharges(id: string) {
    return AxiosService.get<ChargeEntity[]>(`${ON_HAND_BASE_URL}/charges`, {
      params: {
        onHandId: id,
      },
    });
  }

  getDefaultValues() {
    return AxiosService.get<Pick<OnHandEntity, 'origin'>>(`${ON_HAND_BASE_URL}/defaultValues`);
  }

  finalize(onHandId: OnHandFinalizePayload) {
    return AxiosService.put<OnHandFinalizeResponse>(`${ON_HAND_BASE_URL}/finalize/${onHandId}`);
  }

  transferCustomer(payload: TransferCustomerPayload) {
    return AxiosService.put(`${ON_HAND_BASE_URL}/transferCustomer`, payload);
  }
}

export default new OnHandService();
